import {
  CopyOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  LinkedinOutlined,
  MailOutlined,
  MessageOutlined,
  SaveOutlined,
  SlackOutlined,
  TagOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  message,
  Modal,
  notification,
  Popconfirm,
  Popover,
  Row,
  Space,
  Spin,
  Table,
  Tooltip,
} from "antd";
import Avatar from "antd/lib/avatar/avatar";
import copy from "copy-to-clipboard";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Link,
  Route,
  Switch,
  useParams,
} from "react-router-dom";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
} from "recharts";
import CartesianGrid from "recharts/lib/cartesian/CartesianGrid";
import Legend from "recharts/lib/component/Legend";

import { getWorkspaceId } from "../../../lib/queries";
import { getSupaClient } from "../../../lib/supabase";
import {
  // eslint-disable-next-line import/named
  appendMemberTag,
  // eslint-disable-next-line import/named
  removeMemberTag,
  setCompanyDetails,
  setCompanyMembers,
  setTeamTagsData,
} from "../../../redux/actions";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import BulkEdit from "../../Members/BulkEdit";
import MemberTags from "../../MemberTags/MemberTags";
import SendDM from "../../SendDM/SendDM";

const dateFormat = "MM/DD/YY";

const CompanyDetails = () => {
  const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);

  const dispatch = useDispatch();
  const { companyId } = useParams();
  const companyData = useSelector(
    (state) => state.companies.company_details[companyId]
  );
  const members = useSelector(
    (state) => state.companies.company_members[companyId]
  );

  const [isMemberSelected, setIsMemberSelected] = useState(false);
  const [loading, setLoading] = useState(true);

  const supabase = getSupaClient();
  const teamId = localStorage.getItem("team-id");
  const [startTime, setStartTime] = useState(
    moment().subtract(30, "d").format(dateFormat)
  );
  const [endTime, setEndTime] = useState(moment().format(dateFormat));
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedMembersId, setSelectedMembersId] = useState([]);
  const [isMassDmVisible, setIsMassDmVisible] = useState(false);
  const teamTags = useSelector((state) => state.teamTags.team_tags);

  const sortTags = (a, b) => {
    const aIds = a.map((tag) => tag.id).sort();
    const bIds = b.map((tag) => tag.id).sort();
    if (aIds > bIds) return 1;
    if (aIds < bIds) return -1;
    return 0;
  };
  const loadTeamTags = async () => {
    const { data, error } = await supabase
      .from("tags")
      .select("id, name, color")
      .eq("team", teamId);
    if (data) {
      // setTeamTags(data);
      dispatch(setTeamTagsData(data));
    }
    if (error) {
      console.log("Error", error);
    }
  };
  const memberTagsChange = (tag, record) => {
    dispatch(
      appendMemberTag({
        companyId,
        slack_id: record.slack_id,
        data: tag,
      })
    );
  };

  const memberTagsRemove = (tag, record) => {
    dispatch(
      removeMemberTag({
        companyId,
        slack_id: record.slack_id,
        data: tag,
      })
    );
  };
  const allColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div>
            <Avatar src={record.image_32} style={{ marginRight: 15 }} />
          </div>
          <div style={{ maxWidth: "220px", display: "flex" }}>
            <div
              style={{ color: "#1890ff", cursor: "pointer" }}
              tabIndex={0}
              role="button"
            >
              <div style={{ alignContent: "flex-end" }}>
                <Space>
                  <Tooltip title="Click to view the company">
                    {record.name}
                  </Tooltip>
                  <Button type="text" href={`https://${record.linkedin}`}>
                    <LinkedinOutlined />
                  </Button>
                  <Button type="text" href={`mailto:${record.email}`}>
                    <MailOutlined />
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        </div>
      ),
      width: "25%",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    // {
    //   title: "Email",
    //   dataIndex: "email",
    //   key: "email",
    //   render: (text, record) => (
    //     <p>{record.email ? record.email : "No email found"}</p>
    //   ),
    //   width: "25%",
    // },
    {
      title: "Tags / Cohorts",
      dataIndex: "tags",
      key: "tags",
      render: (text, record) => (
        <MemberTags
          teamTags={teamTags}
          memberTags={record.tags}
          memberId={record.slack_id}
          onCompanyMemberTagAdded={(tag) => memberTagsChange(tag, record)}
          onCompanyMemberTagRemoved={(tag) => memberTagsRemove(tag, record)}
        />
      ),
      //  sorter: (a, b) => sortTags(a.tags, b.tags),
      width: "25%",
    },
    {
      title: "# convos started",
      dataIndex: "parent_count",
      key: "parent_count",
      sorter: (a, b) => {
        return a.parent_count - b.parent_count;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "# replies sent",
      dataIndex: "thread_count",
      key: "thread_count",
      sorter: (a, b) => {
        return a.thread_count - b.thread_count;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Total messages sent",
      dataIndex: "total_messages_count",
      key: "total_messages_count",
      sorter: (a, b) => {
        return a.total_messages_count - b.total_messages_count;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "# reactions added",
      dataIndex: "reaction_count",
      sorter: (a, b) => {
        return a.reaction_count - b.reaction_count;
      },
      sortDirections: ["descend", "ascend"],
    },
  ];

  const loadCompanyDetail = async (workspaceId) => {
    const params = {
      start_time: startTime,
      end_time: endTime,
      slack_team_id: teamId,
      workspace_id: workspaceId,
      company_id: companyId,
    };
    const { data, error } = await supabase.rpc("company_detail_view", params);
    if (error) {
      console.log("error in loadCompanyDetail", error);
    } else {
      const updatedData = data.map((item) => {
        const temp = item;
        temp.key = item.slack_id;
        temp.user_name = item.name;
        return temp;
      });
      dispatch(
        setCompanyMembers({
          companyId,
          data: updatedData,
        })
      );
    }
  };

  const loadCompanyInfo = async (workspaceId) => {
    const { data, error } = await supabase
      .from("workspaces_companies")
      .select("companies (name, domain, size, industry, linkedin_id)")
      .match({ id: companyId, workspace: workspaceId });
    if (error) {
      console.log("error in loadCompanyInfo", error);
    } else {
      dispatch(
        setCompanyDetails({
          companyId,
          data: data[0].companies,
        })
      );
    }
  };

  const loadPageData = async () => {
    setLoading(true);
    let workspaceId = null;
    if (!companyData) {
      workspaceId = await getWorkspaceId(teamId);
      await loadCompanyInfo(workspaceId);
    }
    if (!members) {
      if (!workspaceId) workspaceId = await getWorkspaceId(teamId);
      await loadCompanyDetail(workspaceId);
    }
    setLoading(false);
  };

  const exportMembers = () => {
    const csvOptions = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      fileName: "Dots exported members",
      // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
    };
    const companyMembers = isMemberSelected ? selectedMembers : members;
    const membersWithTags = companyMembers.map((member) => {
      let tags = [];
      if (member.tags_json) {
        tags = member.tags_json.map((e) => e.name).join(", ");
        return {
          ...member,
          tags,
        };
      }
      if (member.tags) {
        tags = member.tags.map((e) => e.name).join(", ");
        return {
          ...member,
          tags,
        };
      }
      return member;
    });
    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(membersWithTags);
    const args = {
      message: `${companyMembers.length} members info exported!`,
      duration: 1.5,
    };
    notification.open(args);
    window.analytics.track("CSV export", { members: companyMembers.length });
  };
  const copyIds = () => {
    const companyMembers = isMemberSelected ? selectedMembers : members;
    const copyUsers = companyMembers.map((user) => {
      return user.slack_id;
    });
    copy(copyUsers.join("\r\n"));
    const args = {
      message: `${companyMembers.length} Slack IDs copied!`,
      duration: 1.5,
    };
    notification.open(args);
    window.analytics.track("IDs copied", { members: companyMembers.length });
  };
  const copyEmails = () => {
    const companyMembers = isMemberSelected ? selectedMembers : members;
    const copyUsers = companyMembers.map((user) => {
      return user.email;
    });
    copy(copyUsers.join("\r\n"));
    const args = {
      message: `${companyMembers.length} emails copied!`,
      duration: 1.5,
    };
    notification.open(args);
    window.analytics.track("Emails copied", { members: companyMembers.length });
  };

  const Exportmenu = (
    <Menu>
      <Menu.Item key="1" icon={<FileExcelOutlined />} onClick={exportMembers}>
        {isMemberSelected ? "Export selected to CSV" : "Export all to CSV"}
      </Menu.Item>
      <Menu.Item key="2" icon={<CopyOutlined />} onClick={copyIds}>
        {isMemberSelected ? "Copy selected slack IDs" : "Copy all slack IDs"}
      </Menu.Item>
      <Menu.Item key="3" icon={<MailOutlined />} onClick={copyEmails}>
        {isMemberSelected ? "Copy selected emails" : "Copy all slack emails"}
      </Menu.Item>
    </Menu>
  );
  useEffect(() => {
    loadTeamTags();
    loadPageData();
  }, []);
  const sendMassDm = () => {
    const ids = selectedMembers.map((member) => member.slack_id);
    if (ids.length > 100) {
      message.error(
        "You can only DM to 100 members at a time due to Slack's limit. Please schedule in batches of 100 - each batch 5 mins apart"
      );
    } else {
      setSelectedMembersId(ids);
      setIsMassDmVisible(true);
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      // markDone(selectedRows[0])
      setSelectedMembers(selectedRows);

      if (selectedRowKeys.length > 0) {
        setIsMemberSelected(true);
      } else {
        setIsMemberSelected(false);
      }
    },
  };

  const bulkEditOk = async () => {
    setLoading(true);
    let workspaceId = null;
    workspaceId = await getWorkspaceId(teamId);
    await loadCompanyInfo(workspaceId);
    await loadCompanyDetail(workspaceId);
    setLoading(false);
    message.success("Tags added");

    setIsBulkEditModalVisible(false);
  };

  return (
    <>
      <Row style={{ paddingBottom: 30 }}>
        <Col span={8}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <div>
              <Avatar
                src={`//logo.clearbit.com/${companyData?.domain}`}
                style={{ marginRight: 15 }}
              />
            </div>
            <div>
              <p style={{ fontWeight: "bold" }}>{companyData?.name}</p>
            </div>
          </div>
        </Col>
        <Col span={8} offset={8} style={{ textAlign: "end" }}>
          {/* <DateRangePicker /> */}
        </Col>
      </Row>
      <Row style={{ paddingBottom: 30 }}>
        <Col span={24}>
          <Card
            title={`All Members (${members ? members?.length : "loading"})`}
            bordered={false}
            extra={
              <Space>
                {/* <span style={{ fontStyle: "italic", color: "gray" }}>
                  (Selected: {10})
                </span> */}
                {isMemberSelected ? (
                  <>
                    <Tooltip title="Coming soon!">
                      <Button
                        size="small"
                        onClick={sendMassDm}
                        icon={<MessageOutlined />}
                      >
                        Mass DM
                      </Button>
                    </Tooltip>
                    {/* <Tooltip title="Coming soon!"> */}
                    <Modal
                      title="Bulk edit"
                      visible={isBulkEditModalVisible}
                      onOk={bulkEditOk}
                      onCancel={() => setIsBulkEditModalVisible(false)}
                      footer={[]}
                    >
                      <BulkEdit
                        selectedMembers={selectedMembers}
                        handleSave={bulkEditOk}
                      />
                    </Modal>
                    <Button
                      onClick={() => setIsBulkEditModalVisible(true)}
                      icon={<TagOutlined />}
                      size="small"
                    >
                      Bulk tag members
                    </Button>
                    {/* </Tooltip> */}
                  </>
                ) : null}
                <Tooltip title="Coming soon!">
                  <Popover
                    //   content={filterAddSearch}
                    title="Add a filter"
                    trigger="click"
                    placement="bottomRight"
                    //   visible={visible}
                    //   onVisibleChange={handleVisibleChange}
                  >
                    <Button size="small">Filter</Button>
                  </Popover>
                </Tooltip>
                <Dropdown overlay={Exportmenu}>
                  <Button type="primary" size="small" icon={<SaveOutlined />}>
                    Export
                  </Button>
                </Dropdown>
              </Space>
            }
          >
            <div style={{ width: "100%", height: "100%" }}>
              <Spin spinning={loading}>
                <DateRangePicker
                  loadPageData={loadPageData}
                  setStartTime={setStartTime}
                  setEndTime={setEndTime}
                  customOption={{ disabled: false }}
                  rangeOfSelector={{
                    range: "day",
                    selectorDefaultOptions: [
                      { value: 7, disabled: false },
                      {
                        value: 30,
                        disabled: false,
                      },
                    ],
                  }}
                />
                <Table
                  onChange={(pagination, filters, sorter, extra) => {
                    // setTableFiltered(extra.currentDataSource.length);
                  }}
                  dataSource={members}
                  size="small"
                  columns={allColumns}
                  rowKey="record_id"
                  // rowSelection={{ type: "checkbox" }}
                  rowSelection={{ type: "checkbox", ...rowSelection }}
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ["50", "100", "200"],
                  }}
                />
              </Spin>
            </div>
          </Card>
        </Col>
      </Row>
      <Modal
        title={`Send DMs to ${selectedMembersId.length} selected members`}
        visible={isMassDmVisible}
        onCancel={() => setIsMassDmVisible(false)}
        footer={[]}
      >
        <SendDM
          hideSelectMembers
          closeModal={() => setIsMassDmVisible(false)}
          selectableUsers={members}
          sendNow
          selectedUsers={selectedMembersId}
        />
      </Modal>
    </>
  );
};

export default CompanyDetails;
